import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ShrinkButton } from './Shrinkable';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, Check, X } from 'lucide-react';
import '../tdv3.css';

/* ============================== Button ==============================
   The one button. Primary gets a darker bottom edge that compresses on
   press (the single Duolingo move we adopt — see PLAN.md). */

// Flat, Brilliant-grade buttons (2026-07-07 audit): no gimmick edges, three honest
// sizes with real heights, disabled states built in.
const VARIANTS = {
  primary:
    'bg-primary-500 hover:bg-primary-600 active:bg-primary-700 text-white border-none shadow-sm ' +
    'disabled:bg-neutral-200 disabled:text-neutral-400 disabled:shadow-none disabled:cursor-not-allowed',
  secondary:
    'bg-white hover:bg-neutral-50 active:bg-neutral-100 text-ink border border-neutral-300 ' +
    'disabled:text-neutral-300 disabled:cursor-not-allowed',
  tertiary: 'bg-transparent text-primary-600 hover:underline border-none p-0',
  ink: 'bg-ink hover:bg-neutral-800 active:bg-black text-white border-none shadow-sm',
  // The armed second click of a destructive confirm (remove a student).
  danger:
    'bg-error-600 hover:bg-error-700 active:bg-error-700 text-white border-none shadow-sm ' +
    'disabled:bg-neutral-200 disabled:text-neutral-400 disabled:shadow-none disabled:cursor-not-allowed',
};

const SIZES = {
  // Arbitrary padding (2026-10-06): Bootstrap's px-4 / px-5 are !important and
  // larger (24 / 48px), which made every sm / md button over-wide.
  sm: 'text-[13px] h-9 px-[16px]',
  md: 'text-sm h-10 px-[20px]',
  // Onboarding/stepper CTA: bottom-pinned, Duolingo-scale
  lg: 'text-[15px] font-bold h-12 px-8 min-w-[320px]',
  // Dialog footers (2026-10-06). Arbitrary padding because Bootstrap's
  // px-5 (3rem !important) beats Tailwind's on any portaled dialog; labels
  // never wrap, the footer wraps whole buttons instead.
  dlg: 'text-sm h-10 px-[16px] whitespace-nowrap',
};

export function Button({ variant = 'secondary', size = 'md', className = '', children, ...props }) {
  const base =
    variant === 'tertiary'
      ? 'font-semibold cursor-pointer text-sm '
      : 'inline-flex items-center justify-center gap-1.5 font-semibold cursor-pointer rounded-[8px] ' +
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-300 ' +
        SIZES[size] + ' ';
  return (
    <button className={base + VARIANTS[variant] + ' ' + className} {...props}>
      {children}
    </button>
  );
}

/* ============================== Modal ============================== */

const MODAL_WIDTHS = { sm: 420, md: 560, lg: 780 };

// Dialogs in the conversation (2026-10-14, Sandy: "it wouldn't open up a tab
// for them, it would open that into the conversation"). Inside an InlineDialog
// provider (Buffy's chat), Modal draws its dialog in place as a card in the
// reply instead of a pop-up over the page: same content, same buttons, same
// confirms. Its close button folds it away.
export const InlineDialog = createContext(false);

export function Modal({ open, onClose, size = 'md', children, className = '', labelledBy, describedBy }) {
  const inline = useContext(InlineDialog);
  useEffect(() => {
    if (!open || inline) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, inline]);

  if (!open) return null;
  if (inline) {
    return <div className={'buffy-inline-dialog flex flex-col font-sans antialiased text-ink ' + className} role="group" aria-labelledby={labelledBy} aria-describedby={describedBy}>{children}</div>;
  }
  // Portal to body: escapes any ancestor stacking context (the site navbar sits at z-1000).
  // p-[20px], not p-5 (2026-10-06): Bootstrap's p-5 is 3rem !important, which
  // squeezed every dialog to 279px on a 375px phone.
  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-[20px] font-sans antialiased text-ink" style={{ zIndex: 1100 }}>
      <div className="tdv3-fade absolute inset-0 bg-slate-900/[.45]" onClick={onClose} />
      <div
        className={
          'tdv3-pop-in relative max-w-[94vw] max-h-[90vh] flex flex-col bg-white rounded-[12px] border border-neutral-200 ' +
          'shadow-[0_24px_60px_rgba(0,0,0,.18)] overflow-hidden ' + className
        }
        style={{ width: MODAL_WIDTHS[size] }}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

// Default 24px sides / 16px top and bottom (2026-10-06), matching dialog bodies;
// arbitrary values because Bootstrap's px-5 / py-4 (!important) beat Tailwind's.
export function ModalHeader({ title, sub, onClose, children, pad = 'px-[24px] py-[16px]', titleId, subId }) {
  return (
    <div className={`flex items-center gap-3 ${pad} border-b border-neutral-200 flex-none`}>
      <div className="flex-1 min-w-0">
        <div id={titleId} className="text-lg font-bold text-ink leading-tight [overflow-wrap:anywhere]">{title}</div>
        {sub && <div id={subId} className="text-[13px] text-neutral-500 mt-0.5 [overflow-wrap:anywhere]">{sub}</div>}
      </div>
      {children}
      <ShrinkButton />
      <button
        onClick={onClose}
        aria-label="Close"
        className="border-none bg-neutral-100 text-neutral-600 cursor-pointer w-8 h-8 rounded-[6px] flex-none flex items-center justify-center hover:bg-neutral-200 active:scale-[0.98]"
      >
        <X size={15} strokeWidth={2} />
      </button>
    </div>
  );
}

/* ============================== Toasts ============================== */

const ToastContext = createContext(() => {});
let toastId = 0;

// Toasts sit above Buffy (2026-10-19): bottom-right is where the docked chat
// box and the pill live, and a toast there covered the send button. Anything
// marked data-toast-above is measured when a toast shows.
const TOAST_GAP = 20;
function toastBottom() {
  try {
    const tops = [...document.querySelectorAll('[data-toast-above]')]
      .map((el) => el.getBoundingClientRect().top)
      .filter((top) => top > 0 && top < window.innerHeight);
    return tops.length ? Math.max(TOAST_GAP, Math.round(window.innerHeight - Math.min(...tops) + 12)) : TOAST_GAP;
  } catch { return TOAST_GAP; }
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [bottom, setBottom] = useState(TOAST_GAP);
  const timers = useRef({});

  const dismiss = useCallback((id) => {
    setToasts((t) => t.filter((x) => x.id !== id));
    clearTimeout(timers.current[id]);
    delete timers.current[id];
  }, []);

  const push = useCallback((message, { icon = 'check' } = {}) => {
    const id = ++toastId;
    setBottom(toastBottom());
    setToasts((t) => [...t.slice(-3), { id, message, icon }]);
    timers.current[id] = setTimeout(() => dismiss(id), 4200);
  }, [dismiss]);

  return (
    <ToastContext.Provider value={push}>
      {children}
      {createPortal(
      <div className="tdv3-noprint fixed right-5 flex flex-col gap-2 items-end font-sans antialiased" role="status" aria-live="polite" style={{ zIndex: 1200, bottom }}>
        {toasts.map((t) => (
          <div
            key={t.id}
            className="tdv3-toast-in flex items-center gap-2.5 bg-white border border-neutral-200 rounded-[8px] shadow-[0_8px_24px_rgba(0,0,0,.12)] pl-3 pr-2 py-2.5 max-w-[340px]"
          >
            <span className="w-5 h-5 rounded-full bg-success-600 text-white flex items-center justify-center flex-none">
              <Check size={12} strokeWidth={3} />
            </span>
            <span className="text-[13px] font-semibold text-ink leading-snug">{t.message}</span>
            <button
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
              className="border-none bg-transparent text-neutral-400 hover:text-neutral-600 cursor-pointer p-1 flex-none"
            >
              <X size={13} strokeWidth={2.5} />
            </button>
          </div>
        ))}
      </div>,
      document.body
      )}
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);

/* ============================== SlideDeck ==============================
   The "animated PowerPoint" primitive: slides, dots, back/next, arrow keys.
   Slide content re-mounts on change so tdv3-fade-up / tdv3-stagger replay. */

export function SlideDeck({ slides, onDone, doneLabel = 'Done', footerLeft = null }) {
  const [idx, setIdx] = useState(0);
  const last = idx === slides.length - 1;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' && !last) setIdx((i) => i + 1);
      if (e.key === 'ArrowLeft' && idx > 0) setIdx((i) => i - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, last]);

  return (
    <>
      <div className="flex-1 overflow-y-auto px-6 py-5 min-h-[300px]">
        <div key={idx} className="tdv3-fade-up">
          {slides[idx].content}
        </div>
      </div>
      <div className="flex items-center gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50 flex-none">
        {footerLeft}
        <div className="flex-1" />
        <div className="flex items-center gap-1.5 mr-2">
          {slides.map((s, i) => (
            <button
              key={s.key || i}
              onClick={() => setIdx(i)}
              aria-label={`Slide ${i + 1}`}
              className={
                'border-none cursor-pointer p-0 rounded-full transition-all ' +
                (i === idx ? 'w-5 h-2.5 bg-primary-500' : 'w-2.5 h-2.5 bg-neutral-300 hover:bg-neutral-400')
              }
            />
          ))}
        </div>
        {idx > 0 && (
          <Button variant="secondary" onClick={() => setIdx(idx - 1)}>
            <ArrowLeft size={16} strokeWidth={2.5} /> Back
          </Button>
        )}
        {last ? (
          <Button variant="primary" onClick={onDone}>{doneLabel}</Button>
        ) : (
          <Button variant="primary" onClick={() => setIdx(idx + 1)}>
            Next <ArrowRight size={16} strokeWidth={2.5} />
          </Button>
        )}
      </div>
    </>
  );
}

/* ============================== Confetti ============================== */

const CONFETTI_COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#8F00FF', '#ef4444', '#ffbb00'];

export function ConfettiBurst() {
  return (
    <div className="pointer-events-none absolute inset-x-0 top-2 h-0 flex justify-center" aria-hidden="true">
      {Array.from({ length: 12 }, (_, i) => (
        <span
          key={i}
          className="tdv3-confetti absolute w-1.5 h-2.5 rounded-sm"
          style={{
            '--stagger-i': i,
            left: `${18 + i * 5.5}%`,
            background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
          }}
        />
      ))}
    </div>
  );
}

/* Stagger helper: wraps children rows with incremental reveal delays. */
export function Stagger({ items, render, className = '' }) {
  return (
    <div className={className}>
      {items.map((item, i) => (
        <div key={i} className="tdv3-stagger" style={{ '--stagger-i': i }}>
          {render(item, i)}
        </div>
      ))}
    </div>
  );
}
